import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'

import type { ReactElement } from 'react'
import type { IAppSummary } from '~/types/application'
import type { IAsset } from '~/types/assets-management'
import type { IPageContainer, IPageContainerCreateUpdate } from '~/types/page-container'
import type { ITemplate } from '~/types/template'

import PageContainerForm from '../Form'
import DetailsHeader from '../Form/DetailsHeader'
import TemplateNotFound from '../TemplateNotFound'

import MwMLoader from '~/components/MwMLoader'
import { useTemplateConfigWithTranslationValues } from '~/hooks/useTemplateConfig'
import { useAssetDetailsQueries } from '~/queries/assets'
import { useIapListBySkuQuery } from '~/queries/iap'
import {
  pageContainersKeys,
  useCompilePageContainerMutation,
  useProcessPageContainerMutation,
  useUpdatePageContainerFromTemplateMutation,
} from '~/queries/page-containers'
import { useTranslationValuesQuery } from '~/queries/translation'
import { formatPageContainerData } from '~/utils/page-container'
import { displayToast } from '~/utils/toast'

const PageContainerEdition = ({
  pageContainer,
  selectedApp,
  template,
}: {
  pageContainer: IPageContainer
  selectedApp: IAppSummary
  template: ITemplate
}): ReactElement => {
  const navigate = useNavigate()

  const queryClient = useQueryClient()

  const { template_key: templateKey, template_version: templateVersion } = pageContainer

  const { isLoading: isTranslationLoading, templateConfig } = useTemplateConfigWithTranslationValues({
    applicationSummary: selectedApp,
    templateKey,
  })

  const skus = pageContainer.placeholder_iap_values?.map(({ id }) => id).filter((id) => !!id) || []

  const embeddedSkus =
    pageContainer.placeholder_embedded_ui_values?.reduce((prev, { iaps }) => {
      iaps.forEach((iap) => prev.push(iap))
      return prev
    }, [] as string[]) || []

  const { data: iaps, isLoading: isIapListLoading } = useIapListBySkuQuery(
    {
      appKey: selectedApp.app_key,
      deviceType: selectedApp.device_type,
      skus: [...skus, ...embeddedSkus] as string[],
    },
    { enabled: !!selectedApp.app_key && !!selectedApp.device_type && (!!skus.length || !!embeddedSkus.length) }
  )

  const assetIds = pageContainer?.placeholder_asset_values?.map(({ id }) => id || '')

  const assetQueries = useAssetDetailsQueries(assetIds)

  const assets = assetQueries.some((e) => e.isLoading) ? [] : assetQueries.map(({ data }) => data as IAsset)

  const translationKeys = pageContainer?.placeholder_text_values?.map(({ translation_key }) => translation_key) || []

  const { data: translationsData } = useTranslationValuesQuery({ application: selectedApp, translationKeys })

  const pageContainerDetails = formatPageContainerData({
    assets,
    iaps,
    pageContainer,
    translations: translationsData || [],
  })

  const { mutateAsync: updatePageContainerMutation } = useUpdatePageContainerFromTemplateMutation()

  const { mutateAsync: processPageContainerMutation } = useProcessPageContainerMutation()

  const { mutateAsync: compilePageContainerMutation } = useCompilePageContainerMutation()

  const handleSubmit = async (data: IPageContainerCreateUpdate) => {
    try {
      await updatePageContainerMutation({ data, id: pageContainerDetails.uuid })
      queryClient.invalidateQueries({ queryKey: pageContainersKeys.details(pageContainerDetails.uuid) })
      displayToast('Page container successfully updated', 'success')
    } catch {
      displayToast('Page container could not be updated', 'error')
    }
  }

  const handleProcess = async () => {
    try {
      await processPageContainerMutation(pageContainerDetails.uuid)
      await compilePageContainerMutation(pageContainerDetails.uuid)
      displayToast('Page container successfully compiled', 'success')
      navigate(0)
    } catch {
      displayToast('Page container could not be compiled', 'error')
    }
  }

  const handleCompile = async () => {
    try {
      await compilePageContainerMutation(pageContainerDetails.uuid)
      displayToast('Page container successfully compiled', 'success')
      navigate(0)
    } catch {
      displayToast('Page container could not be compiled', 'error')
    }
  }

  const isLoading =
    isTranslationLoading ||
    (isIapListLoading && (skus?.length || embeddedSkus?.length)) ||
    assetQueries.some((e) => e.isLoading)

  if (isLoading) {
    return (
      <div className="no-template-container">
        <MwMLoader category="item" />
      </div>
    )
  }

  return templateConfig ? (
    <PageContainerForm
      appSummary={selectedApp}
      isUnity={pageContainerDetails.is_unity}
      onCompile={handleCompile}
      onProcess={handleProcess}
      onSubmit={handleSubmit}
      pageContainerDetails={pageContainerDetails}
      template={template}
      templateConfig={templateConfig}
      templateVersion={templateVersion}
    />
  ) : (
    <>
      <DetailsHeader pageContainer={pageContainerDetails} status={pageContainer.status} title={pageContainer.title} />
      <TemplateNotFound pageContainer={pageContainer} selectedApp={selectedApp} templateKey={templateKey} />
    </>
  )
}

export default PageContainerEdition
